import { createContext, useCallback, useContext, useState } from "react";

// Tiny toast system. The <ToastProvider> at the root of the tree holds a
// list of active toasts; any component can call useToast().push(msg) to add
// one. Toasts auto-dismiss after a few seconds.
//
// Each toast: { id, msg, emoji, kind, undo, undoLabel }
//   kind ∈ 'info' (yellow) | 'success' (green) | 'warn' (amber) | 'error'
//   undo: optional () => void. When present, the toast renders an Undo
//     button that fires the callback and dismisses the toast immediately.
//     Use this for any auto-action where the user might disagree with what
//     we did (auto-migrate fridge, auto-categorize, auto-rename, etc.) so
//     reverting is one tap away.
//   undoLabel: optional override for the Undo button text (default "Undo").

const ToastContext = createContext({ push: () => {}, toasts: [] });

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((msg, {
    emoji = "🔔",
    kind = "info",
    ttl = 4500,
    undo = null,
    undoLabel = "Undo",
  } = {}) => {
    const id = crypto.randomUUID();
    setToasts(t => [...t, { id, msg, emoji, kind, undo, undoLabel }]);
    // Toasts with an Undo affordance live a beat longer (default 6s
    // vs 4.5s) so the user has time to spot + tap the button before
    // it slides away. Callers can still pass a custom ttl to override.
    const effectiveTtl = undo && ttl === 4500 ? 6000 : ttl;
    setTimeout(() => {
      setToasts(t => t.filter(x => x.id !== id));
    }, effectiveTtl);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss, toasts }}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

// Stack of toasts pinned above the bottom nav (nav is 80px tall).
function ToastHost({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 96,
        left: 0, right: 0,
        maxWidth: 480, margin: "0 auto",
        padding: "0 14px",
        display: "flex", flexDirection: "column",
        gap: 8,
        zIndex: 220,
        pointerEvents: "none",
      }}
    >
      {toasts.map(t => (
        <div
          key={t.id}
          role={t.undo ? "alert" : "status"}
          style={{
            pointerEvents: "auto",
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 10px 10px 14px",
            background: kindBg(t.kind),
            border: `1px solid ${kindBorder(t.kind)}`,
            borderRadius: 12,
            color: "#f0ece4",
            boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
            animation: "toastIn 0.2s ease",
          }}
        >
          {/* Body — clicking the body dismisses the toast (legacy
              behavior). Undo button is a separate target so a tap
              inside it doesn't double-fire dismiss. */}
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            style={{
              flex: 1, minWidth: 0,
              display: "flex", alignItems: "center", gap: 10,
              padding: "2px 0",
              background: "transparent",
              border: "none",
              textAlign: "left",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 13,
              color: "inherit",
            }}
          >
            {t.emoji ? (
              <span style={{ fontSize: 20, flexShrink: 0 }}>{t.emoji}</span>
            ) : null}
            <span style={{ flex: 1, lineHeight: 1.4 }}>{t.msg}</span>
          </button>

          {t.undo ? (
            <button
              type="button"
              onClick={() => {
                try { t.undo(); } catch (err) { console.warn("[toast] undo handler threw:", err); }
                onDismiss(t.id);
              }}
              style={{
                flexShrink: 0,
                padding: "8px 14px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 8,
                color: "#f7e7c8",
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              {t.undoLabel || "Undo"}
            </button>
          ) : null}

          <style>{`@keyframes toastIn { from { transform: translateY(8px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
        </div>
      ))}
    </div>
  );
}

function kindBg(kind) {
  if (kind === "success") return "#15201a";
  if (kind === "warn")    return "#201a10";
  if (kind === "error")   return "#2a1515";
  return "#1a1a1a";
}
function kindBorder(kind) {
  if (kind === "success") return "#2a3a2e";
  if (kind === "warn")    return "#3a2e1e";
  if (kind === "error")   return "#3a1e1e";
  return "#2a2a2a";
}
